import { AlertTriangle, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import type { BuildStep, WorldGraphBuild } from '../types'

const PROVIDER_LABELS: Record<string, string> = {
  claude: 'Claude',
  openai: 'OpenAI',
}

const RUNNING_LABELS: Record<BuildStep, string> = {
  sidecar: 'Writing the baseline context…',
  stocks: 'Proposing the stocks…',
  links: 'Linking the stocks…',
  finalize: 'Proposing how the uncontrolled stocks drift…',
  proposals: "Proposing each team's changes from its decisions…",
  artifacts: 'Writing the news and briefs…',
}

const TYPICAL_TIME: Record<BuildStep, string> = {
  sidecar: 'usually for two or three minutes',
  stocks: 'usually for a few minutes, longer if it has to ask for more stocks',
  links: 'usually for a few minutes',
  finalize: 'usually for under a minute',
  proposals: 'usually a minute or two, a little longer with many teams',
  artifacts: 'usually for one to three minutes',
}

export function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`
}

export function billingNote(build: WorldGraphBuild): string {
  const provider = PROVIDER_LABELS[build.provider] || build.provider
  const key =
    build.key_source === 'room' ? "this room's key" : "the server's key"
  return `${provider}, billed to ${key}`
}

function Notes({ notes }: { notes: string[] }) {
  if (!notes.length) return null
  return (
    <ul
      className='list-disc space-y-0.5 pl-5 text-xs text-muted-foreground'
      data-testid='world-graph-build-notes'
    >
      {notes.map((note, index) => (
        <li key={index}>{note}</li>
      ))}
    </ul>
  )
}

/** A step being written in the background, with what it has done so far. */
export function BuildProgress({ build }: { build: WorldGraphBuild }) {
  return (
    <Card data-testid='world-graph-build-progress'>
      <CardContent className='space-y-4 p-6'>
        <div className='flex flex-wrap items-center gap-2 text-sm text-dark-blue'>
          <Loader2 className='h-4 w-4 animate-spin' aria-hidden />
          <span className='font-medium'>{RUNNING_LABELS[build.step]}</span>
          <span className='text-muted-foreground'>
            {formatElapsed(build.elapsed_seconds)}
            {build.progress_chars > 0 &&
              ` · ${build.progress_chars.toLocaleString()} characters so far`}
          </span>
        </div>
        <p className='text-xs text-muted-foreground'>
          This runs on the server, {TYPICAL_TIME[build.step]} (
          {billingNote(build)}). You can leave this page and come back.
        </p>
        <Notes notes={build.notes} />
        <Skeleton className='h-7 w-1/2' />
        <div className='space-y-2'>
          <Skeleton className='h-4 w-full' />
          <Skeleton className='h-4 w-[92%]' />
          <Skeleton className='h-4 w-[97%]' />
        </div>
      </CardContent>
    </Card>
  )
}

/** Why the last run of a step did not finish. */
export function LastRunAlert({ build }: { build: WorldGraphBuild }) {
  return (
    <Alert variant='destructive'>
      <AlertTriangle className='h-4 w-4' />
      <AlertTitle>
        {build.status === 'interrupted'
          ? 'The last run was interrupted'
          : 'The last run did not finish'}
      </AlertTitle>
      <AlertDescription className='space-y-2'>
        <p>{build.error}</p>
        <Notes notes={build.notes} />
      </AlertDescription>
    </Alert>
  )
}

/** How a finished run went: its time, its bill, and anything it changed. */
export function RunSummary({
  build,
  verb,
}: {
  build: WorldGraphBuild
  verb: string
}) {
  return (
    <div className='space-y-1'>
      <p className='text-xs text-muted-foreground'>
        {verb} in {formatElapsed(build.elapsed_seconds)} ({billingNote(build)}).
      </p>
      <Notes notes={build.notes} />
    </div>
  )
}

/** The latest run, if it belongs to these steps. */
export function runFor(
  build: WorldGraphBuild | null,
  ...steps: BuildStep[]
): WorldGraphBuild | null {
  return build && steps.includes(build.step) ? build : null
}
